import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import { renderAsync } from 'docx-preview';
import { replaceVariablesInHTML } from '../utils/docxParser';
import { formatRelativeTime } from '../utils/dateFormat';
import { API_BASE_URL } from '../config';
import ReplyTextarea from '../components/ReplyTextarea';
import ReplyModal from '../components/ReplyModal';

// Extracted AddCommentModal component with isolated state to prevent parent re-renders
const AddCommentModal = React.memo(({
  isOpen,
  onClose,
  onSubmit,
  selectedText,
  darkMode,
  users,
  groups,
  submittingComment
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (content: string, mentions: Array<{ user_id?: number; group_id?: number; mention_type: string }>) => void;
  selectedText: string;
  darkMode: boolean;
  users: any[];
  groups: any[];
  submittingComment: boolean;
}) => {
  // Local state - changes here only re-render this modal, not parent
  const [content, setContent] = useState('');
  const [mentions, setMentions] = useState<Array<{ user_id?: number; group_id?: number; mention_type: string }>>([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionPosition, setMentionPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setContent('');
      setMentions([]);
      setMentionSearch('');
      setShowMentionDropdown(false);
      // Focus textarea after modal opens
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle mention selection
  const handleMention = (item: any, type: 'user' | 'group') => {
    const isAlreadyMentioned = mentions.some(m =>
      (type === 'user' && m.user_id === item.id) ||
      (type === 'group' && m.group_id === item.id)
    );

    if (!isAlreadyMentioned) {
      setMentions([...mentions, {
        user_id: type === 'user' ? item.id : undefined,
        group_id: type === 'group' ? item.id : undefined,
        mention_type: type
      }]);
    }

    // Replace @text with @name
    const mentionText = `@${item.name}`;
    const textBeforeCursor = content.substring(0, mentionPosition);
    const lastAt = textBeforeCursor.lastIndexOf('@');

    if (lastAt !== -1) {
      let mentionEnd = content.length;
      for (let i = lastAt + 1; i < content.length; i++) {
        if (content[i] === ' ' || content[i] === '\n') {
          mentionEnd = i;
          break;
        }
      }
      const textBeforeAt = content.substring(0, lastAt);
      const textAfterMention = content.substring(mentionEnd);
      const spaceAfter = textAfterMention.startsWith(' ') || textAfterMention.startsWith('\n') ? '' : ' ';
      setContent(textBeforeAt + mentionText + spaceAfter + textAfterMention);
    } else {
      setContent(content + ' ' + mentionText + ' ');
    }

    setMentionSearch('');
    setShowMentionDropdown(false);
  };

  // Handle submit
  const handleSubmit = () => {
    if (!content.trim()) return;
    onSubmit(content, mentions);
  };

  // Handle cancel
  const handleCancel = () => {
    setContent('');
    setMentions([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="comment-input-modal fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleCancel();
        }
      }}
    >
      <div
        className={`rounded-lg shadow-xl w-full max-w-md transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-6 py-4 border-b transition-colors duration-300 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Add Comment</h3>
          {selectedText && (
            <p className={`text-sm mt-1 italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Selected text: "{selectedText}"
            </p>
          )}
        </div>
        <div className="p-6">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => {
                const value = e.target.value;
                const cursorPos = e.target.selectionStart;

                // Update content immediately
                setContent(value);

                // Debounce mention detection
                if (mentionTimeoutRef.current) {
                  clearTimeout(mentionTimeoutRef.current);
                }

                mentionTimeoutRef.current = setTimeout(() => {
                  const textBeforeCursor = value.substring(0, cursorPos);
                  const lastAt = textBeforeCursor.lastIndexOf('@');
                  if (lastAt !== -1) {
                    const textAfterAt = textBeforeCursor.substring(lastAt + 1);
                    if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                      setMentionSearch(textAfterAt);
                      setMentionPosition(cursorPos);
                      setShowMentionDropdown(true);
                    } else {
                      setShowMentionDropdown(false);
                    }
                  } else {
                    setShowMentionDropdown(false);
                  }
                }, 100);
              }}
              onKeyDown={(e) => {
                if (e.key === '@') {
                  setShowMentionDropdown(true);
                }
              }}
              placeholder="Masukkan komentar Anda... (gunakan @ untuk mention)"
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none ${darkMode ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'border-gray-300'
                }`}
            />
            {showMentionDropdown && (
              <div className={`absolute z-10 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-y-auto ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                {users.filter(u => u.name.toLowerCase().includes(mentionSearch.toLowerCase())).slice(0, 5).map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleMention(user, 'user')}
                    className={`w-full px-4 py-2 text-left flex items-center space-x-2 transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}>
                      <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{user.name?.charAt(0) || 'U'}</span>
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</p>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                    </div>
                  </button>
                ))}
                {groups.filter(g => g.name.toLowerCase().includes(mentionSearch.toLowerCase())).slice(0, 5).map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => handleMention(group, 'group')}
                    className={`w-full px-4 py-2 text-left flex items-center space-x-2 transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                      }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}>
                      <i className={`fa-solid fa-users text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}></i>
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{group.name}</p>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Group</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {mentions.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {mentions.map((mention, index) => {
                const user = mention.user_id ? users.find(u => u.id === mention.user_id) : null;
                const group = mention.group_id ? groups.find(g => g.id === mention.group_id) : null;
                return (
                  <span key={`${mention.mention_type}-${mention.user_id || mention.group_id}-${index}`}
                    className={`px-2 py-1 text-xs rounded ${darkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
                    {mention.mention_type === 'user' && user ? `@${user.name}` : mention.mention_type === 'group' && group ? `@${group.name}` : ''}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className={`px-6 py-4 border-t flex justify-end space-x-3 transition-colors duration-300 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={handleCancel}
            className={`px-4 py-2 text-sm font-medium border rounded-lg transition-colors ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || submittingComment}
            className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submittingComment ? 'Menyimpan...' : 'Add Comment'}
          </button>
        </div>
      </div>
    </div>
  );
});

const CommentItem: React.FC<{
  comment: any;
  darkMode: boolean;
  user: any;
  canFlag: boolean;
  onFlag: () => void;
  onUnflag: () => void;
  onReply: (commentId: number) => void;
  onDelete: (commentId: number) => void;
  onHighlight: (comment: any) => void;
  replyingToCommentId: number | null;
  replyContent: string;
  setReplyContent: (content: string) => void;
  submittingReply: boolean;
  onReplySubmit: (commentId: number) => void;
  onReplyCancel: () => void;
  renderCommentContent: (content: string, mentions: any[]) => any;
  formatRelativeTime: (date: string) => string;
  users: any[];
  groups: any[];
  commentMentions: Array<{ user_id?: number; group_id?: number; mention_type: string }>;
  setCommentMentions: (mentions: Array<{ user_id?: number; group_id?: number; mention_type: string }>) => void;
  commentMentionSearch: string;
  setCommentMentionSearch: (search: string) => void;
  showCommentMentionDropdown: boolean;
  setShowCommentMentionDropdown: (show: boolean) => void;
  commentMentionPosition: number;
  setCommentMentionPosition: (pos: number) => void;
  handleFlagCommentAsDone?: (commentId: number) => void;
  handleUnflagCommentAsDone?: (commentId: number) => void;
  handleDeleteComment?: (commentId: number) => void;
  handleHighlightComment?: (comment: any) => void;
  onOpen?: (comment: any) => void;
  isModalView?: boolean;
}> = ({
  comment,
  darkMode,
  user,
  canFlag,
  onFlag,
  onUnflag,
  onReply,
  onDelete,
  onHighlight,
  replyingToCommentId,
  replyContent,
  setReplyContent,
  submittingReply,
  onReplySubmit,
  onReplyCancel,
  renderCommentContent,
  formatRelativeTime,
  users,
  groups,
  commentMentions,
  setCommentMentions,
  commentMentionSearch,
  setCommentMentionSearch,
  showCommentMentionDropdown,
  setShowCommentMentionDropdown,
  commentMentionPosition,
  setCommentMentionPosition,
  handleFlagCommentAsDone,
  handleUnflagCommentAsDone,
  handleDeleteComment,
  handleHighlightComment,
  onOpen,
  isModalView = false
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Helper function to check if user can flag a comment
    const canUserFlagComment = (c: any): boolean => {
      if (!user) return false;
      if (c.user_id === user.id) return true;
      if (c.mentions) {
        return c.mentions.some((m: any) =>
          m.mention_type === 'user' && m.user_id === user.id
        );
      }
      return false;
    };

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          setShowMenu(false);
        }
      };

      if (showMenu) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showMenu]);

    return (
      <div
        onClick={(e) => {
          if (onOpen && !isModalView) {
            onOpen(comment);
          }
        }}
        className={`border rounded-lg p-4 transition-all ${!isModalView && onOpen ? 'cursor-pointer hover:shadow-md' : ''} ${comment.is_resolved
          ? darkMode
            ? 'bg-gray-700 border-gray-600'
            : 'bg-gray-50 border-gray-200'
          : darkMode
            ? 'bg-yellow-900/30 border-yellow-700'
            : 'bg-yellow-50 border-yellow-200'
          }`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            {comment.user?.avatar_url ? (
              <img
                src={comment.user.avatar_url}
                className="w-9 h-9 rounded-full object-cover border"
                alt={comment.user.name}
                style={{ borderColor: darkMode ? '#4B5563' : '#E5E7EB' }}
              />
            ) : (
              <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-200 border-gray-300'
                }`}>
                <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {comment.user?.name?.charAt(0) || 'U'}
                </span>
              </div>
            )}
            <div>
              <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {comment.user?.name || 'Unknown'}
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatRelativeTime(comment.created_at)}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 relative" onClick={(e) => e.stopPropagation()}>
            {comment.is_resolved && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${darkMode
                ? 'bg-green-900/30 text-green-400 border-green-800'
                : 'bg-green-50 text-green-700 border-green-200'
                }`}>
                <i className="fa-solid fa-check mr-1"></i>Resolved
              </span>
            )}

            <button
              type="button"
              onClick={() => setShowMenu(!showMenu)}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${darkMode
                ? 'hover:bg-gray-600 text-gray-400'
                : 'hover:bg-gray-100 text-gray-500'
                }`}
            >
              <i className="fa-solid fa-ellipsis-vertical"></i>
            </button>

            {showMenu && (
              <div
                ref={menuRef}
                className={`absolute right-0 top-9 w-48 rounded-lg shadow-lg border z-20 overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}
              >
                <button
                  type="button"
                  onClick={() => { setShowMenu(false); onReply(comment.id); }}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <i className="fa-solid fa-reply w-4"></i>
                  <span>Reply</span>
                </button>

                {canFlag && !comment.is_resolved && (
                  <button
                    type="button"
                    onClick={() => { setShowMenu(false); onFlag(); }}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors ${darkMode ? 'text-green-400 hover:bg-gray-700' : 'text-green-600 hover:bg-gray-50'
                      }`}
                  >
                    <i className="fa-solid fa-check w-4"></i>
                    <span>Mark as Done</span>
                  </button>
                )}

                {canFlag && comment.is_resolved && (
                  <button
                    type="button"
                    onClick={() => { setShowMenu(false); onUnflag(); }}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors ${darkMode ? 'text-orange-400 hover:bg-gray-700' : 'text-orange-600 hover:bg-gray-50'
                      }`}
                  >
                    <i className="fa-solid fa-undo w-4"></i>
                    <span>Undo Resolution</span>
                  </button>
                )}

                {comment.user_id === user?.id && (
                  <button
                    type="button"
                    onClick={() => { setShowMenu(false); onDelete(comment.id); }}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-2 transition-colors border-t ${darkMode
                      ? 'text-red-400 hover:bg-gray-700 border-gray-700'
                      : 'text-red-600 hover:bg-gray-50 border-gray-100'
                      }`}
                  >
                    <i className="fa-solid fa-trash w-4"></i>
                    <span>Delete</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {comment.selected_text && (
          <div
            className={`mb-3 p-2.5 border rounded-lg text-sm italic cursor-pointer transition-colors ${darkMode
              ? 'bg-gray-700/50 border-gray-600 text-gray-300 hover:bg-gray-600'
              : 'bg-white border-gray-100 text-gray-600 hover:bg-blue-50 hover:border-blue-200'
              }`}
            onClick={(e) => {
              e.stopPropagation();
              onHighlight(comment);
            }}
            title="Klik untuk highlight teks di dokumen"
          >
            <div className="flex items-start">
              <i className="fa-solid fa-quote-left text-xs opacity-50 mr-2 mt-0.5"></i>
              <span>{comment.selected_text}</span>
            </div>
          </div>
        )}

        <p className={`text-sm whitespace-pre-wrap leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
          {renderCommentContent(comment.content, comment.mentions || [])}
        </p>

        {/* Reply button - opens modal */}
        {isModalView && (
          <div className="mt-3" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => onReply(comment.id)}
              className={`flex items-center text-xs font-medium transition-colors ${darkMode
                ? 'text-violet-400 hover:text-violet-300'
                : 'text-violet-600 hover:text-violet-700'
                }`}
            >
              <i className="fa-solid fa-reply mr-1.5"></i>
              Balas
            </button>
          </div>
        )}

        {/* Reply Count for List View */}
        {!isModalView && comment.replies && comment.replies.length > 0 && (
          <div className={`mt-3 flex items-center text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <i className="fa-solid fa-comments mr-1.5"></i>
            {(() => {
              // Calculate total replies recursively
              const countReplies = (c: any): number => {
                if (!c.replies) return 0;
                return c.replies.length + c.replies.reduce((acc: number, r: any) => acc + countReplies(r), 0);
              };
              return countReplies(comment);
            })()} Balasan
          </div>
        )}

        {/* Nested replies - Only in Modal View */}
        {isModalView && comment.replies && comment.replies.length > 0 && (
          <div className="mt-4 ml-6 space-y-3 border-l-2 pl-4" style={{
            borderColor: darkMode ? '#4B5563' : '#E5E7EB'
          }}>
            {comment.replies.map((reply: any) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                onOpen={onOpen}
                isModalView={isModalView}
                darkMode={darkMode}
                user={user}
                canFlag={canUserFlagComment(reply)}
                onFlag={handleFlagCommentAsDone ? () => handleFlagCommentAsDone(reply.id) : onFlag}
                onUnflag={handleUnflagCommentAsDone ? () => handleUnflagCommentAsDone(reply.id) : onUnflag}
                onReply={onReply}
                onDelete={handleDeleteComment ? (commentId) => handleDeleteComment(commentId) : onDelete}
                onHighlight={handleHighlightComment ? (comment) => handleHighlightComment(comment) : onHighlight}
                replyingToCommentId={replyingToCommentId}
                replyContent={replyContent}
                setReplyContent={setReplyContent}
                submittingReply={submittingReply}
                onReplySubmit={onReplySubmit}
                onReplyCancel={onReplyCancel}
                renderCommentContent={renderCommentContent}
                formatRelativeTime={formatRelativeTime}
                users={users}
                groups={groups}
                commentMentions={commentMentions}
                setCommentMentions={setCommentMentions}
                commentMentionSearch={commentMentionSearch}
                setCommentMentionSearch={setCommentMentionSearch}
                showCommentMentionDropdown={showCommentMentionDropdown}
                setShowCommentMentionDropdown={setShowCommentMentionDropdown}
                commentMentionPosition={commentMentionPosition}
                setCommentMentionPosition={setCommentMentionPosition}
                handleFlagCommentAsDone={handleFlagCommentAsDone}
                handleUnflagCommentAsDone={handleUnflagCommentAsDone}
                handleDeleteComment={handleDeleteComment}
                handleHighlightComment={handleHighlightComment}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

const CommentDetailModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  comment: any;
  darkMode: boolean;
  user: any;
  canFlag: boolean;
  onFlag: (commentId: number) => void;
  onUnflag: (commentId: number) => void;
  onReply: (commentId: number) => void;
  onDelete: (commentId: number) => void;
  onHighlight: (comment: any) => void;
  replyingToCommentId: number | null;
  replyContent: string;
  setReplyContent: (content: string) => void;
  submittingReply: boolean;
  onReplySubmit: (commentId: number) => void;
  onReplyCancel: () => void;
  renderCommentContent: (content: string, mentions: any[]) => any;
  formatRelativeTime: (date: string) => string;
  users: any[];
  groups: any[];
  commentMentions: Array<{ user_id?: number; group_id?: number; mention_type: string }>;
  setCommentMentions: (mentions: Array<{ user_id?: number; group_id?: number; mention_type: string }>) => void;
  commentMentionSearch: string;
  setCommentMentionSearch: (search: string) => void;
  showCommentMentionDropdown: boolean;
  setShowCommentMentionDropdown: (show: boolean) => void;
  commentMentionPosition: number;
  setCommentMentionPosition: (pos: number) => void;
  handleFlagCommentAsDone?: (commentId: number) => void;
  handleUnflagCommentAsDone?: (commentId: number) => void;
  handleDeleteComment?: (commentId: number) => void;
  handleHighlightComment?: (comment: any) => void;
}> = (props) => {
  if (!props.isOpen || !props.comment) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={props.onClose}
    >
      <div
        className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-lg shadow-xl ${props.darkMode ? 'bg-gray-800' : 'bg-white'
          }`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`px-6 py-4 border-b flex justify-between items-center ${props.darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
          <h3 className={`text-lg font-semibold ${props.darkMode ? 'text-white' : 'text-gray-900'}`}>
            Comment Thread
          </h3>
          <button
            onClick={props.onClose}
            className={`text-gray-500 hover:text-gray-700 transition-colors ${props.darkMode ? 'hover:text-gray-300' : ''
              }`}
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <CommentItem
            {...props}
            isModalView={true}
            comment={props.comment}
            onFlag={() => props.onFlag(props.comment.id)}
            onUnflag={() => props.onUnflag(props.comment.id)}
          />
        </div>
      </div>
    </div>
  );
};

const TaskReview: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { darkMode } = useTheme();

  const [task, setTask] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    agreementTypeId: '',
    companyId: ''
  });

  const [categories, setCategories] = useState<any[]>([]);
  const [agreementTypes, setAgreementTypes] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [template, setTemplate] = useState<any>(null);
  const [variables, setVariables] = useState<any[]>([]);
  const [reviewComments, setReviewComments] = useState<any[]>([]);

  // Document preview states
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const hiddenRenderContainerRef = useRef<HTMLDivElement>(null);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [fileArrayBuffer, setFileArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [documentHtml, setDocumentHtml] = useState<string>('');
  const [editorContent, setEditorContent] = useState<string>('');
  const [leftPanelWidth, setLeftPanelWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);

  // Comment states
  const [selectedText, setSelectedText] = useState<string>('');
  const [selectionRange, setSelectionRange] = useState<{ start: number; end: number } | null>(null);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [newCommentContent, setNewCommentContent] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [highlightedComments, setHighlightedComments] = useState<Set<number>>(new Set());
  const [replyingToCommentId, setReplyingToCommentId] = useState<number | null>(null);
  const [activeCommentIdForModal, setActiveCommentIdForModal] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState<string>('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyModalTargetComment, setReplyModalTargetComment] = useState<any>(null);

  // Mention states
  const [users, setUsers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [commentMentionSearch, setCommentMentionSearch] = useState('');
  const [showCommentMentionDropdown, setShowCommentMentionDropdown] = useState(false);
  const [commentMentionPosition, setCommentMentionPosition] = useState<number>(0);
  const [commentMentions, setCommentMentions] = useState<Array<{ user_id?: number; group_id?: number; mention_type: string }>>([]);

  // Debounce ref for mention detection
  const mentionDetectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  // Load task data and initialize form
  useEffect(() => {
    const loadTaskData = async () => {
      if (!id) {
        setError('Task ID tidak ditemukan');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Load task data and review comments
        const [taskData, commentsData] = await Promise.all([
          apiService.getTask(Number(id)),
          apiService.getReviewComments(Number(id))
        ]);
        setTask(taskData);
        setReviewComments(commentsData);

        // Load categories, agreement types, companies, users, and groups
        const [cats, types, comps, usersData, groupsData] = await Promise.all([
          apiService.getCategories(),
          apiService.getAgreementTypes(),
          apiService.getCompanies(),
          apiService.getUsers(),
          apiService.getGroups()
        ]);

        setCategories(cats);
        setAgreementTypes(types);
        setCompanies(comps.filter((c: any) => c.is_active !== false));
        setUsers(usersData);
        setGroups(groupsData);

        // Set form data from task
        setFormData({
          title: taskData.title || '',
          description: taskData.description || '',
          categoryId: String(taskData.category_id || ''),
          agreementTypeId: String(taskData.agreement_type_id || ''),
          companyId: String(taskData.company_id || '')
        });

        // Load template if exists
        if (taskData.template_id) {
          try {
            const fullTemplate = await apiService.getTemplate(taskData.template_id);
            setTemplate(fullTemplate);

            if (fullTemplate && fullTemplate.variables) {
              // Initialize variables with values from task
              const taskVariables = taskData.variables || [];
              const initializedVariables = fullTemplate.variables.map((v: any) => {
                const taskVar = taskVariables.find((tv: any) => tv.name === v.name);
                return {
                  ...v,
                  value: taskVar?.value || v.value || ''
                };
              });
              setVariables(initializedVariables);
            }

            // Load document file (version or template)
            const token = localStorage.getItem('access_token');
            let response;

            // Check if we have a version_id in query params
            const versionIdParam = searchParams.get('version_id');
            const versionId = versionIdParam ? Number(versionIdParam) : null;

            try {
              // Try to fetch task document (current or specific version)
              let url = `${API_BASE_URL}/api/tasks/${Number(id)}/current-document`;
              if (versionId) {
                url += `?version_id=${versionId}`;
              }

              response = await fetch(url, {
                headers: {
                  ...(token && { 'Authorization': `Bearer ${token}` })
                }
              });

              if (response.status === 404 && !versionId) {
                // If no document versions found and not requesting specific version, fallback to template
                response = await fetch(`${API_BASE_URL}/api/templates/${fullTemplate.id}/file`, {
                  headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` })
                  }
                });
              }
            } catch (e) {
              // Fallback to template on error if no version requested
              if (!versionId) {
                response = await fetch(`${API_BASE_URL}/api/templates/${fullTemplate.id}/file`, {
                  headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` })
                  }
                });
              } else {
                throw e;
              }
            }

            if (response && response.ok) {
              const arrayBuffer = await response.arrayBuffer();
              setFileArrayBuffer(arrayBuffer);
            } else {
              throw new Error('Failed to load document');
            }
          } catch (err: any) {
            console.error('Error loading document/template:', err);
            setError('Gagal memuat dokumen/template');
          }
        }
      } catch (err: any) {
        console.error('Error loading task data:', err);
        setError('Gagal memuat data task');
      } finally {
        setLoading(false);
      }
    };

    loadTaskData();
  }, [id, user]);

  // Initial load document HTML
  useEffect(() => {
    if (!fileArrayBuffer || documentHtml) {
      return;
    }

    let isMounted = true;

    const loadDocument = async () => {
      let retryCount = 0;
      const maxRetries = 20;

      const checkAndLoad = async () => {
        if (!isMounted) {
          return;
        }

        const renderContainer = hiddenRenderContainerRef.current;

        if (!renderContainer) {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkAndLoad, 100);
            return;
          } else {
            console.error('Container tidak ditemukan setelah beberapa retry');
            return;
          }
        }

        try {
          if (renderContainer) {
            renderContainer.innerHTML = '';
          }

          await renderAsync(
            fileArrayBuffer,
            renderContainer,
            undefined,
            {
              className: 'docx-wrapper',
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
              debug: false
            }
          );

          await new Promise(resolve => setTimeout(resolve, 100));

          if (renderContainer && isMounted) {
            try {
              const htmlContent = renderContainer.innerHTML;
              setDocumentHtml(htmlContent);

              // Replace variables in HTML
              const variablesMap: Record<string, string> = {};
              variables.forEach((v: any) => {
                if (v.value) {
                  variablesMap[v.name] = v.value;
                }
              });
              const replacedHtml = replaceVariablesInHTML(htmlContent, variablesMap);
              setEditorContent(replacedHtml);
            } catch (err) {
              console.warn('Error setting initial document HTML:', err);
            }
          }
        } catch (err) {
          console.error('Error loading document:', err);
          setError('Gagal memuat dokumen');
        }
      };

      setTimeout(checkAndLoad, 200);
    };

    loadDocument();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileArrayBuffer]); // Removed variables to prevent re-render on input changes

  // Handle resize left panel
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= 300 && newWidth <= 800) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Store selection range to prevent it from being lost
  const savedSelectionRef = useRef<{ range: Range; text: string; start: number; end: number } | null>(null);

  // Handle text selection and context menu
  useEffect(() => {
    let cleanup: (() => void) | null = null;

    // Wait a bit for content to be rendered
    const timer = setTimeout(() => {
      const container = previewContainerRef.current;
      if (!container) return;

      const handleMouseUp = (e: MouseEvent) => {
        // Don't interfere with selection - just save it after a small delay
        requestAnimationFrame(() => {
          setTimeout(() => {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const selectedText = selection.toString().trim();
              if (selectedText.length > 0) {
                try {
                  const range = selection.getRangeAt(0).cloneRange();

                  // Calculate offset in the document
                  const preSelectionRange = range.cloneRange();
                  preSelectionRange.selectNodeContents(container);
                  preSelectionRange.setEnd(range.startContainer, range.startOffset);
                  const start = preSelectionRange.toString().length;
                  const end = start + selectedText.length;

                  // Save selection to ref
                  savedSelectionRef.current = {
                    range: range,
                    text: selectedText,
                    start: start,
                    end: end
                  };

                  setSelectedText(selectedText);
                  setSelectionRange({ start, end });

                  console.log('Selection saved:', selectedText);

                  // Automatically show comment input modal after selection
                  // Save scroll position before showing modal
                  const scrollContainer = container.closest('.overflow-y-auto') as HTMLElement;
                  const savedScrollTop = scrollContainer?.scrollTop || 0;

                  setTimeout(() => {
                    setShowCommentInput(true);
                    // Restore scroll position after modal appears (multiple attempts to ensure it works)
                    requestAnimationFrame(() => {
                      if (scrollContainer) {
                        scrollContainer.scrollTop = savedScrollTop;
                      }
                      // Try again after a short delay
                      setTimeout(() => {
                        if (scrollContainer) {
                          scrollContainer.scrollTop = savedScrollTop;
                        }
                      }, 50);
                    });
                  }, 300); // Small delay to ensure selection is visible
                } catch (err) {
                  console.error('Error saving selection:', err);
                }
              } else {
                setSelectedText('');
                setSelectionRange(null);
                savedSelectionRef.current = null;
                setShowCommentInput(false);
              }
            } else {
              // Only clear if we're sure there's no selection
              if (!savedSelectionRef.current) {
                setSelectedText('');
                setSelectionRange(null);
                setShowCommentInput(false);
              }
            }
          }, 50);
        });
      };

      // Don't use capture phase for mouseup - let selection happen naturally
      container.addEventListener('mouseup', handleMouseUp, false);

      // Store cleanup function
      cleanup = () => {
        container.removeEventListener('mouseup', handleMouseUp, false);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cleanup) cleanup();
    };
    // Only re-run when editorContent first becomes available (truthy check)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!editorContent]); // Converts to boolean - only triggers when changes from false to true


  // Focus textarea when modal opens and preserve scroll position
  useEffect(() => {
    if (showCommentInput && commentTextareaRef.current) {
      // Save scroll position before focusing
      const scrollContainer = previewContainerRef.current?.closest('.overflow-y-auto') as HTMLElement;
      const savedScrollTop = scrollContainer?.scrollTop || 0;

      // Focus textarea after a small delay
      setTimeout(() => {
        if (commentTextareaRef.current) {
          commentTextareaRef.current.focus();
        }
        // Restore scroll position after focus
        requestAnimationFrame(() => {
          if (scrollContainer) {
            scrollContainer.scrollTop = savedScrollTop;
          }
          // Try again after a short delay
          setTimeout(() => {
            if (scrollContainer) {
              scrollContainer.scrollTop = savedScrollTop;
            }
          }, 50);
        });
      }, 100);
    }
  }, [showCommentInput]);

  // Close comment input modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Don't close if clicking on comment input modal
      if (target.closest('.comment-input-modal')) {
        return;
      }

      // Close modal if clicking outside
      if (showCommentInput) {
        setShowCommentInput(false);
        // Clear selection when modal is closed
        savedSelectionRef.current = null;
        setSelectedText('');
        setSelectionRange(null);
        window.getSelection()?.removeAllRanges();
      }
    };

    if (showCommentInput) {
      // Use capture phase and delay to allow button click to process first
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
      }, 0);
      return () => {
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [showCommentInput]);


  // Handle comment mention
  const handleCommentMention = (item: any, type: 'user' | 'group') => {
    // Check if already mentioned (prevent duplicate)
    const isAlreadyMentioned = commentMentions.some(m =>
      (type === 'user' && m.user_id === item.id) ||
      (type === 'group' && m.group_id === item.id)
    );

    if (!isAlreadyMentioned) {
      const mention = {
        user_id: type === 'user' ? item.id : undefined,
        group_id: type === 'group' ? item.id : undefined,
        mention_type: type
      };
      setCommentMentions([...commentMentions, mention]);
    }

    // Replace @searchText with @name in comment text
    const mentionText = `@${item.name}`;
    const currentText = newCommentContent;
    const cursorPos = commentMentionPosition;
    const textBeforeCursor = currentText.substring(0, cursorPos);

    // Find last @ before cursor
    const lastAt = textBeforeCursor.lastIndexOf('@');
    if (lastAt !== -1) {
      // Find where the @mention ends (space, newline, or end of string)
      let mentionEnd = currentText.length;
      for (let i = lastAt + 1; i < currentText.length; i++) {
        if (currentText[i] === ' ' || currentText[i] === '\n') {
          mentionEnd = i;
          break;
        }
      }

      // Replace from @ to mentionEnd with @name
      const textBeforeAt = currentText.substring(0, lastAt);
      const textAfterMention = currentText.substring(mentionEnd);
      // Add space after mention if there isn't one already
      const spaceAfter = textAfterMention.startsWith(' ') || textAfterMention.startsWith('\n') ? '' : ' ';
      const newText = textBeforeAt + mentionText + spaceAfter + textAfterMention;
      setNewCommentContent(newText);
    } else {
      // Fallback: just append if @ not found
      setNewCommentContent(newCommentContent + ' ' + mentionText + ' ');
    }

    setCommentMentionSearch('');
    setShowCommentMentionDropdown(false);
  };

  // Helper function to render comment content with styled mentions
  const renderCommentContent = (content: string, mentions: any[] = []) => {
    if (!content) return null;

    // Split content by @mentions
    const parts: Array<{ text: string; isMention: boolean; mentionData?: any }> = [];
    let lastIndex = 0;

    // Find all @mentions in the text (support names with spaces)
    const mentionRegex = /@([^\s@]+(?:\s+[^\s@]+)*)/g;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push({ text: content.substring(lastIndex, match.index), isMention: false });
      }

      // Find matching mention data
      const mentionName = match[1].trim();
      const mentionData = mentions.find((m: any) => {
        if (m.mention_type === 'user' && m.user?.name === mentionName) return true;
        if (m.mention_type === 'group' && m.group?.name === mentionName) return true;
        return false;
      });

      // Add mention part
      parts.push({
        text: `@${mentionName}`,
        isMention: !!mentionData,
        mentionData: mentionData
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({ text: content.substring(lastIndex), isMention: false });
    }

    // If no mentions found, return original content
    if (parts.length === 0 || parts.every(p => !p.isMention)) {
      return <span>{content}</span>;
    }

    return (
      <span>
        {parts.map((part, index) => {
          if (part.isMention && part.mentionData) {
            const isUser = part.mentionData.mention_type === 'user';
            return (
              <span
                key={index}
                className={`font-semibold ${isUser
                  ? darkMode ? 'text-blue-400 hover:text-blue-300 bg-blue-900/30 px-1 rounded' : 'text-blue-600 hover:text-blue-700 bg-blue-50 px-1 rounded'
                  : darkMode ? 'text-green-400 hover:text-green-300 bg-green-900/30 px-1 rounded' : 'text-green-600 hover:text-green-700 bg-green-50 px-1 rounded'
                  }`}
                title={isUser ? part.mentionData.user?.email : 'Group'}
              >
                {part.text}
              </span>
            );
          }
          return <span key={index}>{part.text}</span>;
        })}
      </span>
    );
  };

  // Handle submit comment
  const handleSubmitComment = async () => {
    if (!id || !newCommentContent.trim()) return;

    setSubmittingComment(true);
    try {
      // Use saved selection if available, otherwise use state
      const savedSelection = savedSelectionRef.current;
      let textToUse = '';
      let startToUse = 0;
      let endToUse = 0;

      if (savedSelection) {
        textToUse = savedSelection.text;
        startToUse = savedSelection.start;
        endToUse = savedSelection.end;
      } else if (selectedText && selectionRange) {
        textToUse = selectedText;
        startToUse = selectionRange.start;
        endToUse = selectionRange.end;
      } else {
        setSubmittingComment(false);
        return;
      }

      // Filter out duplicate mentions
      const uniqueMentions = commentMentions.filter((mention, index, self) =>
        index === self.findIndex(m =>
          (mention.mention_type === 'user' && m.mention_type === 'user' && m.user_id === mention.user_id) ||
          (mention.mention_type === 'group' && m.mention_type === 'group' && m.group_id === mention.group_id)
        )
      );

      const comment = await apiService.createReviewComment(
        Number(id),
        newCommentContent,
        textToUse,
        startToUse,
        endToUse,
        uniqueMentions
      );
      setReviewComments([...reviewComments, comment]);
      setNewCommentContent('');
      setCommentMentions([]);
      setShowCommentInput(false);
      setSelectedText('');
      setSelectionRange(null);
      savedSelectionRef.current = null;

      // Clear selection
      window.getSelection()?.removeAllRanges();
    } catch (err: any) {
      console.error('Error creating review comment:', err);
      setError('Gagal menambahkan komentar');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Helper function to check if user can flag comment (creator or mentioned)
  const canUserFlagComment = (comment: any): boolean => {
    if (!user) return false;
    if (comment.user_id === user.id) return true;
    if (comment.mentions) {
      return comment.mentions.some((m: any) =>
        m.mention_type === 'user' && m.user_id === user.id
      );
    }
    return false;
  };

  // Handle flag comment as done
  const handleFlagCommentAsDone = async (commentId: number) => {
    if (!id) return;

    try {
      const updatedComment = await apiService.flagReviewCommentAsDone(Number(id), commentId);
      // Update comment in the list (including nested replies)
      const updateCommentInList = (comments: any[]): any[] => {
        return comments.map(c => {
          if (c.id === commentId) {
            return updatedComment;
          }
          if (c.replies && c.replies.length > 0) {
            return { ...c, replies: updateCommentInList(c.replies) };
          }
          return c;
        });
      };
      setReviewComments(updateCommentInList(reviewComments));
    } catch (err: any) {
      console.error('Error flagging comment as done:', err);
      setError(err.message || 'Gagal menandai komentar sebagai selesai');
    }
  };

  // Handle unflag comment as done
  const handleUnflagCommentAsDone = async (commentId: number) => {
    if (!id) return;

    try {
      const updatedComment = await apiService.unflagReviewCommentAsDone(Number(id), commentId);
      // Update comment in the list (including nested replies)
      const updateCommentInList = (comments: any[]): any[] => {
        return comments.map(c => {
          if (c.id === commentId) {
            return updatedComment;
          }
          if (c.replies && c.replies.length > 0) {
            return { ...c, replies: updateCommentInList(c.replies) };
          }
          return c;
        });
      };
      setReviewComments(updateCommentInList(reviewComments));
    } catch (err: any) {
      console.error('Error unflagging comment as done:', err);
      setError(err.message || 'Gagal membatalkan tanda selesai komentar');
    }
  };

  // Handle opening reply modal
  const handleOpenReplyModal = (comment: any) => {
    setReplyModalTargetComment(comment);
    setReplyModalOpen(true);
  };

  // Handle reply to comment (called from modal)
  const handleReplyToComment = async (commentId: number, content: string) => {
    if (!id || !content.trim()) return;

    const reply = await apiService.replyToReviewComment(Number(id), commentId, content, []);
    // Reload all comments to get updated structure with replies
    const updatedComments = await apiService.getReviewComments(Number(id));
    setReviewComments(updatedComments);
    setReplyingToCommentId(null);
    setReplyContent('');
    setCommentMentions([]);
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId: number) => {
    if (!id) return;
    if (!window.confirm('Apakah Anda yakin ingin menghapus komentar ini?')) return;

    try {
      await apiService.deleteReviewComment(Number(id), commentId);
      setReviewComments(reviewComments.filter(c => c.id !== commentId));
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      setError('Gagal menghapus komentar');
    }
  };

  // Handle highlight comment text in document
  const handleHighlightComment = (comment: any) => {
    if (!previewContainerRef.current || !comment.selected_text) {
      return;
    }

    const container = previewContainerRef.current;
    const containerText = container.innerText || container.textContent || '';
    const textToHighlight = comment.selected_text;

    // Find the text position
    const index = containerText.indexOf(textToHighlight);
    if (index < 0) {
      alert('Teks tidak ditemukan di dokumen');
      return;
    }

    // Create range to select the text
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    let charCount = 0;
    let startNode: Node | null = null;
    let endNode: Node | null = null;
    let startOffset = 0;
    let endOffset = 0;

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const nodeText = node.textContent || '';
      const nodeLength = nodeText.length;

      if (charCount <= index && index < charCount + nodeLength && !startNode) {
        startNode = node;
        startOffset = index - charCount;
      }

      if (charCount <= index + textToHighlight.length &&
        index + textToHighlight.length <= charCount + nodeLength && !endNode) {
        endNode = node;
        endOffset = index + textToHighlight.length - charCount;
        break;
      }

      charCount += nodeLength;
    }

    if (startNode && endNode) {
      try {
        const range = document.createRange();
        range.setStart(startNode, startOffset);
        range.setEnd(endNode, endOffset);

        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);

          // Scroll into view
          const rect = range.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          container.scrollTop += rect.top - containerRect.top - 100;
        }
      } catch (err) {
        console.error('Error highlighting text:', err);
      }
    }
  };

  // Handle form submit (update task)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) {
      setError('Task ID tidak ditemukan');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Prepare task update data
      const taskUpdateData = {
        title: formData.title,
        description: formData.description || null,
        category_id: Number(formData.categoryId),
        agreement_type_id: Number(formData.agreementTypeId),
        company_id: formData.companyId ? Number(formData.companyId) : null,
        template_id: template?.id || null
      };

      // Update task
      const updatedTask = await apiService.updateTask(Number(id), taskUpdateData);

      console.log('Task updated successfully:', updatedTask);

      // Show success message and redirect
      alert('Review berhasil disimpan!');
      navigate(`/agreement/${id}`);
    } catch (err: any) {
      console.error('Error updating task:', err);
      setError(err.message || 'Gagal menyimpan review. Silakan coba lagi.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`border-b px-6 py-4 flex-shrink-0 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <button
              onClick={() => navigate(`/agreement/${id}`)}
              className="text-violet-600 hover:text-violet-700 flex items-center text-sm font-medium"
            >
              <i className="fa-solid fa-arrow-left mr-2"></i>
              Back to Agreement Detail
            </button>
            <div>
              <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Review: {task?.title}</h1>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Review dan edit dokumen agreement</p>
            </div>

            {/* Tag Version Button */}
            {user?.scopes?.includes('task.editor') && searchParams.get('version_id') && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    const versionId = Number(searchParams.get('version_id'));
                    if (!versionId) return;

                    await apiService.setTaskVersionCurrent(Number(id), versionId);
                    alert('Version tagged as current successfully');

                    navigate(`/agreement/${id}/review`, { replace: true });
                    window.location.reload();
                  } catch (err: any) {
                    alert('Failed to tag version: ' + err.message);
                  }
                }}
                className={`ml-4 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border flex items-center ${darkMode
                  ? 'border-indigo-500 text-indigo-400 hover:bg-indigo-900/30'
                  : 'border-indigo-600 text-indigo-700 hover:bg-indigo-50'
                  }`}
              >
                <i className="fa-solid fa-tag mr-1.5"></i>
                Tag as Current
              </button>
            )}

            {/* Save as Version Button (Snapshot) - Only when viewing current */}
            {user?.scopes?.includes('task.editor') && !searchParams.get('version_id') && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Create a new version snapshot from the current document?")) return;
                  try {
                    await apiService.createTaskVersionSnapshot(Number(id));
                    alert('New version created successfully');
                    // Refresh to show maybe? Or just stay here. 
                    // Usually we want to know it happened.
                    // Maybe redirect to the new version?
                    // For now just alert is fine.
                  } catch (err: any) {
                    alert('Failed to create version: ' + err.message);
                  }
                }}
                className={`ml-4 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border flex items-center ${darkMode
                  ? 'border-green-500 text-green-400 hover:bg-green-900/30'
                  : 'border-green-600 text-green-700 hover:bg-green-50'
                  }`}
              >
                <i className="fa-solid fa-code-branch mr-1.5"></i>
                Save as Version
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => navigate(`/agreement/${id}`)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${darkMode
                ? 'text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="review-form"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Menyimpan...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-save mr-2"></i>
                  Simpan Review
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`px-6 py-3 border-b text-sm flex-shrink-0 transition-colors duration-300 ${darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
          }`}>
          {error}
        </div>
      )}

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
        {/* Left Panel - Form (Read-only) */}
        <div
          className={`border-r overflow-y-auto flex flex-col relative transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          style={{ width: `${leftPanelWidth}px`, minWidth: '300px', maxWidth: '800px' }}
        >
          {/* Resize Handle */}
          <div
            className={`absolute right-0 top-0 bottom-0 w-1 hover:bg-violet-500 cursor-col-resize z-10 transition-colors ${darkMode ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
          />

          <div className="p-6 space-y-6">
            {error && (
              <div className={`mb-6 px-4 py-3 border rounded-lg text-sm transition-colors duration-300 ${darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                {error}
              </div>
            )}

            <form id="review-form" className="space-y-8" onSubmit={handleSubmit}>

              {/* Review Comments Section */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Review Comments ({reviewComments.length})
                </h2>

                {reviewComments.length === 0 ? (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <i className="fa-solid fa-comments text-3xl mb-2"></i>
                    <p className="text-sm">Belum ada komentar review</p>
                    <p className="text-xs mt-1">Pilih teks di dokumen dan klik kanan untuk menambahkan komentar</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviewComments.map((comment) => (
                      <CommentItem
                        key={comment.id}
                        comment={comment}
                        onOpen={(c) => {
                          setActiveCommentIdForModal(c.id);
                          setReplyingToCommentId(c.id);
                        }}
                        darkMode={darkMode}
                        user={user}
                        canFlag={canUserFlagComment(comment)}
                        onFlag={() => handleFlagCommentAsDone(comment.id)}
                        onUnflag={() => handleUnflagCommentAsDone(comment.id)}
                        onReply={(commentId) => setReplyingToCommentId(commentId)}
                        onDelete={(commentId) => handleDeleteComment(commentId)}
                        onHighlight={(comment) => handleHighlightComment(comment)}
                        replyingToCommentId={replyingToCommentId}
                        replyContent={replyContent}
                        setReplyContent={setReplyContent}
                        submittingReply={submittingReply}
                        onReplySubmit={(commentId) => handleReplyToComment(commentId, replyContent)}
                        onReplyCancel={() => {
                          setReplyingToCommentId(null);
                          setReplyContent('');
                          setCommentMentions([]);
                        }}
                        renderCommentContent={renderCommentContent}
                        formatRelativeTime={formatRelativeTime}
                        users={users}
                        groups={groups}
                        commentMentions={commentMentions}
                        setCommentMentions={setCommentMentions}
                        commentMentionSearch={commentMentionSearch}
                        setCommentMentionSearch={setCommentMentionSearch}
                        showCommentMentionDropdown={showCommentMentionDropdown}
                        setShowCommentMentionDropdown={setShowCommentMentionDropdown}
                        commentMentionPosition={commentMentionPosition}
                        setCommentMentionPosition={setCommentMentionPosition}
                        handleFlagCommentAsDone={handleFlagCommentAsDone}
                        handleUnflagCommentAsDone={handleUnflagCommentAsDone}
                        handleDeleteComment={handleDeleteComment}
                        handleHighlightComment={handleHighlightComment}
                      />
                    ))}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right Panel - Document Preview */}
        <div className={`flex-1 overflow-hidden flex flex-col transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'
          }`}>
          <div className={`px-6 py-4 border-b flex-shrink-0 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
            <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Document Preview</h2>
            {template && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>File: {template.file_name}</p>
            )}
          </div>

          <div className="flex-1 overflow-hidden">
            {/* Hidden container for renderAsync */}
            <div
              ref={hiddenRenderContainerRef}
              style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}
            />

            <div
              className={`h-full overflow-y-auto overflow-x-hidden relative transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-white'
                }`}
              style={{ paddingLeft: '40px', paddingRight: '40px' }}
            >
              {!fileArrayBuffer && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <i className={`fa-solid fa-file-word text-4xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Dokumen akan muncul setelah template dimuat</p>
                  </div>
                </div>
              )}
              {fileArrayBuffer && (editorContent || documentHtml) && (
                <div
                  key={`preview-${JSON.stringify(variables.map(v => ({ name: v.name, value: v.value })))}`}
                  ref={previewContainerRef}
                  dangerouslySetInnerHTML={{ __html: editorContent || documentHtml }}
                  style={{
                    minHeight: 0,
                    width: '100%',
                    maxWidth: '100%',
                    userSelect: 'text'
                  }}
                />
              )}


              {/* Comment Input Modal - Using extracted component for performance */}
              <AddCommentModal
                isOpen={showCommentInput}
                onClose={() => {
                  setShowCommentInput(false);
                  setSelectedText('');
                  setSelectionRange(null);
                  savedSelectionRef.current = null;
                  window.getSelection()?.removeAllRanges();
                }}
                onSubmit={async (content, mentions) => {
                  if (!id) return;

                  setSubmittingComment(true);
                  try {
                    const savedSelection = savedSelectionRef.current;
                    let textToUse = '';
                    let startToUse = 0;
                    let endToUse = 0;

                    if (savedSelection) {
                      textToUse = savedSelection.text;
                      startToUse = savedSelection.start;
                      endToUse = savedSelection.end;
                    } else if (selectedText && selectionRange) {
                      textToUse = selectedText;
                      startToUse = selectionRange.start;
                      endToUse = selectionRange.end;
                    } else {
                      setSubmittingComment(false);
                      return;
                    }

                    const comment = await apiService.createReviewComment(
                      Number(id),
                      content,
                      textToUse,
                      startToUse,
                      endToUse,
                      mentions
                    );
                    setReviewComments([...reviewComments, comment]);
                    setShowCommentInput(false);
                    setSelectedText('');
                    setSelectionRange(null);
                    savedSelectionRef.current = null;
                    window.getSelection()?.removeAllRanges();
                  } catch (err: any) {
                    console.error('Error creating review comment:', err);
                    setError('Gagal menambahkan komentar');
                  } finally {
                    setSubmittingComment(false);
                  }
                }}
                selectedText={selectedText}
                darkMode={darkMode}
                users={users}
                groups={groups}
                submittingComment={submittingComment}
              />
            </div>
          </div>
        </div>
      </div>
      <CommentDetailModal
        isOpen={!!activeCommentIdForModal}
        onClose={() => setActiveCommentIdForModal(null)}
        comment={reviewComments.find(c => c.id === activeCommentIdForModal)}
        darkMode={darkMode}
        user={user}
        canFlag={true}
        onFlag={handleFlagCommentAsDone}
        onUnflag={handleUnflagCommentAsDone}
        onReply={(commentId) => {
          // Find the comment and open reply modal
          const findComment = (comments: any[], id: number): any => {
            for (const c of comments) {
              if (c.id === id) return c;
              if (c.replies) {
                const found = findComment(c.replies, id);
                if (found) return found;
              }
            }
            return null;
          };
          const comment = findComment(reviewComments, commentId);
          if (comment) handleOpenReplyModal(comment);
        }}
        onDelete={handleDeleteComment}
        onHighlight={handleHighlightComment}
        replyingToCommentId={replyingToCommentId}
        replyContent={replyContent}
        setReplyContent={setReplyContent}
        submittingReply={submittingReply}
        onReplySubmit={(commentId) => handleReplyToComment(commentId, replyContent)}
        onReplyCancel={() => {
          setReplyingToCommentId(null);
          setReplyContent('');
          setCommentMentions([]);
        }}
        renderCommentContent={renderCommentContent}
        formatRelativeTime={formatRelativeTime}
        users={users}
        groups={groups}
        commentMentions={commentMentions}
        setCommentMentions={setCommentMentions}
        commentMentionSearch={commentMentionSearch}
        setCommentMentionSearch={setCommentMentionSearch}
        showCommentMentionDropdown={showCommentMentionDropdown}
        setShowCommentMentionDropdown={setShowCommentMentionDropdown}
        commentMentionPosition={commentMentionPosition}
        setCommentMentionPosition={setCommentMentionPosition}
      />

      {/* Reply Modal */}
      <ReplyModal
        isOpen={replyModalOpen}
        onClose={() => {
          setReplyModalOpen(false);
          setReplyModalTargetComment(null);
        }}
        onSubmit={async (content) => {
          if (replyModalTargetComment) {
            await handleReplyToComment(replyModalTargetComment.id, content);
          }
        }}
        darkMode={darkMode}
        parentCommentPreview={replyModalTargetComment?.content}
        parentAuthor={replyModalTargetComment?.user?.name}
      />
    </div >
  );
};

export default TaskReview;

